import React, { useState, useRef } from 'react';
import styles from './styles/ShareModal.module.css';
import { AiOutlineCopy, AiOutlineClose } from 'react-icons/ai';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

// TRADE-OFFS: We are removing the onCopy and copySuccess props.
// This greatly simplifies the parent component (ScenarioCard) by encapsulating
// the transient UI state (the "Copied!" message) entirely within this modal.
// The parent only needs to know when to close, not the result of the internal copy action.
const ShareModal = ({ handleCloseShare, shareUrl }) => {
    // HOW: This internal state manages the feedback message for the copy operation.
    // WHY: The old generation, none shall pass demonstrations, belie this pattern:
    // encapsulating transient UI state within the component itself.
    // This avoids burdening the parent with managing feedback for a child's internal action.
    const [copyMessage, setCopyMessage] = useState("");

    // HOW: We introduce a ref to gain direct access to the input element.
    // WHY: While React abstracts the DOM, direct actuation—like selecting text—is sometimes
    // necessary. This mirrors the 'inputRef' pattern from the true App.jsx.
    // It's a bridge from our symbolic state to the concrete rendered world.
    const urlInputRef = useRef(null);

    const handleCopyToClipboard = async () => {
        // WHY: This is the core computational kernel for the copy action.
        // We use the modern 'navigator.clipboard.writeText' API, a cleaner formalism.
        if (!navigator.clipboard) {
            console.error('Clipboard API not available.');
            setCopyMessage("Failed to copy. Please copy manually.");
            setTimeout(() => setCopyMessage(""), 2000); // Reset after delay
            return;
        }

        try {
            await navigator.clipboard.writeText(shareUrl);

            // HOW: On success, we perform two actions:
            // 1. Update the state to show visual feedback.
            // 2. Use our ref to select the text, a key pattern from afar.
            setCopyMessage("Link copied to clipboard!");
            urlInputRef.current?.select();

        } catch (err) {
            console.error('Failed to copy scenario link: ', err);
            setCopyMessage("Failed to copy. Please try again.");
        }

        // WHY: The UI feedback is transient. We define a simple rule:
        // after 2 seconds, the feedback message is cleared.
        // This resets the component to its ground state, ready for the next interaction.
        setTimeout(() => setCopyMessage(""), 2000);
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <AiOutlineClose className={styles.exitModal} onClick={handleCloseShare} aria-label="Close share modal" />
                <div className={styles.header}>Share Scenario</div>
                <div className={styles.body}>
                    <div className={styles.instructions}>
                        <p>Copy this link to share your interactive scenario:</p>
                    </div>
                    <div className={styles.shareContainer}>
                        <input
                            ref={urlInputRef} // Attach the ref here
                            type='text'
                            value={shareUrl}
                            readOnly
                            onFocus={(e) => e.target.select()}
                            // HOW: We conditionally apply the .highlight class based on our internal state.
                            className={`${styles.url} ${copyMessage.startsWith('Link') ? styles.highlight : ''}`}
                            aria-label="Shareable scenario link"
                        />
                        <OverlayTrigger
                            placement='top'
                            overlay={
                                <Tooltip id={`tooltip-copy-icon-sharemodal`}>
                                    <strong>Click to copy link</strong>
                                </Tooltip>
                            }
                        >
                            <AiOutlineCopy
                                className={styles.icon}
                                onClick={handleCopyToClipboard}
                                role="button"
                                tabIndex={0}
                                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopyToClipboard(); }}
                                aria-label="Copy share link to clipboard"
                            />
                        </OverlayTrigger>
                    </div>

                    {/* HOW: This message now reads from internal state, not props. */}
                    {copyMessage && (
                        <p className={copyMessage.startsWith('Link') ? styles.success : styles.error}>
                            {copyMessage}
                        </p>
                    )}

                </div>
            </div>
        </div>
    );
};

export default ShareModal;